import React, { useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet";
import { Eye, EyeOff, Heart } from "lucide-react";
import {
  get,
  onValue,
  ref as databaseRef,
  update,
} from "firebase/database";
import "../../components/Wedding/wedding.css";
import {
  ensureAnonymousUser,
  firebaseDatabase,
  isFirebaseSecurityConfigured,
} from "../../firebase/client";
import { ImageWithFallback } from "../../components/Wedding/ImageWithFallback";

type PhotoStatus = "approved" | "hidden";

interface AdminPhoto {
  id: string;
  downloadUrl: string;
  storagePath: string;
  cloudinaryPublicId: string;
  status: PhotoStatus;
  createdAt: number;
  approvedAt: number;
  source: string;
  ownerUid: string;
  likes: number;
}

const normalizeAdminPhoto = (id: string, value: any): AdminPhoto | null => {
  if (!value || typeof value.downloadUrl !== "string") {
    return null;
  }

  return {
    id,
    downloadUrl: value.downloadUrl,
    storagePath: String(value.storagePath || ""),
    cloudinaryPublicId: String(value.cloudinaryPublicId || ""),
    status: value.status === "hidden" ? "hidden" : "approved",
    createdAt: Number(value.createdAt) || 0,
    approvedAt: Number(value.approvedAt) || 0,
    source: String(value.source || "camera"),
    ownerUid: String(value.ownerUid || ""),
    likes: Number.isFinite(Number(value.likes)) ? Number(value.likes) : 0,
  };
};

const formatAdminDate = (createdAt: number) => {
  if (!createdAt) {
    return "Unknown date";
  }

  return new Intl.DateTimeFormat("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(createdAt));
};

const configuredAdminUuid = process.env.REACT_APP_WEDDING_ADMIN_UUID;

export default function WeddingAdminManagement() {
  const [photos, setPhotos] = useState<AdminPhoto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [adminUid, setAdminUid] = useState("");
  const [adminCode, setAdminCode] = useState("");
  const [hasAdminAccess, setHasAdminAccess] = useState(false);
  const [accessLoading, setAccessLoading] = useState(false);
  const [busyPhotoIds, setBusyPhotoIds] = useState<Record<string, boolean>>({});
  const [confirmingHideIds, setConfirmingHideIds] = useState<
    Record<string, boolean>
  >({});

  const sortedPhotos = useMemo(
    () => [...photos].sort((left, right) => right.createdAt - left.createdAt),
    [photos],
  );
  const visibleCount = photos.filter((photo) => photo.status === "approved").length;
  const hiddenCount = photos.filter((photo) => photo.status === "hidden").length;

  useEffect(() => {
    let unsubscribe: undefined | (() => void);
    let didCancel = false;

    const loadPhotos = async () => {
      if (!isFirebaseSecurityConfigured || !firebaseDatabase) {
        setError("Firebase security is not configured.");
        setLoading(false);
        return;
      }

      try {
        const user = await ensureAnonymousUser();

        if (didCancel) {
          return;
        }

        setAdminUid(user.uid);
        const photosRef = databaseRef(firebaseDatabase, "weddingPhotos");
        unsubscribe = onValue(
          photosRef,
          (snapshot) => {
            const value = snapshot.val();
            const nextPhotos = value
              ? Object.entries(value)
                  .map(([id, photo]) => normalizeAdminPhoto(id, photo))
                  .filter((photo): photo is AdminPhoto => Boolean(photo))
              : [];

            setPhotos(nextPhotos);
            setError("");
            setLoading(false);
          },
          () => {
            setError("Could not load guest camera photos.");
            setLoading(false);
          },
        );
      } catch (_error) {
        setError("Could not sign in for admin access.");
        setLoading(false);
      }
    };

    loadPhotos();

    return () => {
      didCancel = true;
      unsubscribe?.();
    };
  }, []);

  const setPhotoBusy = (photoId: string, isBusy: boolean) => {
    setBusyPhotoIds((current) => {
      const next = { ...current };

      if (isBusy) {
        next[photoId] = true;
      } else {
        delete next[photoId];
      }

      return next;
    });
  };

  const updatePhotoStatus = async (photo: AdminPhoto, status: PhotoStatus) => {
    if (!firebaseDatabase) {
      return;
    }

    try {
      setPhotoBusy(photo.id, true);
      setError("");
      await update(databaseRef(firebaseDatabase, `weddingPhotos/${photo.id}`), {
        status,
      });
      setConfirmingHideIds((current) => {
        const next = { ...current };
        delete next[photo.id];
        return next;
      });
    } catch (error) {
      const errorMessage =
        error instanceof Error ? ` ${error.message}` : "";
      setError(
        `Could not update this photo. Make sure your UID is added under weddingAdmins in Firebase and the latest database rules are deployed.${errorMessage}`,
      );
    } finally {
      setPhotoBusy(photo.id, false);
    }
  };

  const requestHideConfirmation = (photoId: string) => {
    setConfirmingHideIds((current) => ({ ...current, [photoId]: true }));
  };

  const cancelHideConfirmation = (photoId: string) => {
    setConfirmingHideIds((current) => {
      const next = { ...current };
      delete next[photoId];
      return next;
    });
  };

  const submitAdminCode = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    const trimmedCode = adminCode.trim();

    if (!firebaseDatabase || !adminUid || !trimmedCode) {
      setError("Enter the admin username or UUID before making changes.");
      return;
    }

    try {
      setAccessLoading(true);
      setError("");

      if (trimmedCode === adminUid) {
        setHasAdminAccess(true);
        setAdminCode("");
        return;
      }

      const adminSnapshot = await get(
        databaseRef(firebaseDatabase, `weddingAdmins/${trimmedCode}`),
      );

      if (adminSnapshot.val() !== true) {
        if (configuredAdminUuid) {
          if (trimmedCode !== configuredAdminUuid) {
            throw new Error("Admin username or UUID was not found.");
          }
        } else {
          const codeSnapshot = await get(
            databaseRef(firebaseDatabase, `weddingAdminCodes/${trimmedCode}`),
          );

          if (codeSnapshot.val() !== true) {
            throw new Error("Admin username or UUID was not found.");
          }
        }
      }

      setHasAdminAccess(true);
      setAdminCode("");
    } catch (error) {
      const errorMessage =
        error instanceof Error ? ` ${error.message}` : "";
      setHasAdminAccess(false);
      setError(
        `Could not verify admin access. Enter a weddingAdmins username, current Firebase UID, or configured admin UUID.${errorMessage}`,
      );
    } finally {
      setAccessLoading(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Wedding Photo Admin</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <main className="wedding-admin">
        <div className="wedding-admin__header">
          <div>
            <p className="wedding-admin__eyebrow">Wedding Admin</p>
            <h1 className="wedding-admin__title">Guest Camera Management</h1>
          </div>
          <div className="wedding-admin__stats">
            <span>{photos.length} photos</span>
            <span>{visibleCount} visible</span>
            <span>{hiddenCount} hidden</span>
          </div>
        </div>

        <form className="wedding-admin-access" onSubmit={submitAdminCode}>
          <label className="wedding-admin-access__label" htmlFor="admin-code">
            Admin username or UUID
          </label>
          <div className="wedding-admin-access__row">
            <input
              id="admin-code"
              className="wedding-admin-access__input"
              type="password"
              value={adminCode}
              onChange={(event) => setAdminCode(event.target.value)}
              placeholder="Enter admin username or UUID"
              autoComplete="off"
            />
            <button
              type="submit"
              className="wedding-admin-button wedding-admin-access__button"
              disabled={accessLoading || !adminUid}
            >
              {accessLoading ? "Checking..." : "Unlock"}
            </button>
          </div>
          <p className="wedding-admin-access__hint">
            {hasAdminAccess
              ? "Admin access is unlocked for this browser session."
              : "Enter a weddingAdmins username, current Firebase UID, or shared admin UUID to hide or show photos."}
          </p>
        </form>

        {error && <p className="wedding-admin__error">{error}</p>}

        {loading ? (
          <div className="wedding-admin__empty">Loading photos...</div>
        ) : sortedPhotos.length ? (
          <div className="wedding-admin__grid">
            {sortedPhotos.map((photo) => {
              const isBusy = Boolean(busyPhotoIds[photo.id]);
              const isHidden = photo.status === "hidden";
              const isConfirmingHide = Boolean(confirmingHideIds[photo.id]);

              return (
                <article
                  className={`wedding-admin-card ${
                    isHidden ? "wedding-admin-card--hidden" : ""
                  }`}
                  key={photo.id}
                >
                  <ImageWithFallback
                    src={photo.downloadUrl}
                    alt="Wedding guest camera"
                    className="wedding-admin-card__image"
                  />
                  <div className="wedding-admin-card__body">
                    <div>
                      <span className="wedding-admin-card__status">
                        {isHidden ? "Hidden" : "Visible"}
                      </span>
                      <p className="wedding-admin-card__date">
                        {formatAdminDate(photo.createdAt)}
                      </p>
                    </div>
                    <span className="wedding-admin-card__likes">
                      <Heart size={14} strokeWidth={1.8} />
                      {photo.likes}
                    </span>
                  </div>
                  <div className="wedding-admin-card__actions">
                    {isConfirmingHide && !isHidden ? (
                      <div className="wedding-admin-card__confirm">
                        <p>Hide this photo from the public gallery?</p>
                        <div className="wedding-admin-card__confirm-actions">
                          <button
                            type="button"
                            className="wedding-admin-button wedding-admin-button--confirm"
                            onClick={() => updatePhotoStatus(photo, "hidden")}
                            disabled={isBusy || !hasAdminAccess}
                          >
                            Yes, hide
                          </button>
                          <button
                            type="button"
                            className="wedding-admin-button"
                            onClick={() => cancelHideConfirmation(photo.id)}
                            disabled={isBusy}
                          >
                            Cancel
                          </button>
                        </div>
                      </div>
                    ) : (
                      <button
                        type="button"
                        className="wedding-admin-button"
                        onClick={() =>
                          isHidden
                            ? updatePhotoStatus(photo, "approved")
                            : requestHideConfirmation(photo.id)
                        }
                        disabled={isBusy || !hasAdminAccess}
                      >
                        {isHidden ? (
                          <Eye size={16} strokeWidth={1.8} />
                        ) : (
                          <EyeOff size={16} strokeWidth={1.8} />
                        )}
                        {isHidden ? "Show" : "Hide"}
                      </button>
                    )}
                  </div>
                </article>
              );
            })}
          </div>
        ) : (
          <div className="wedding-admin__empty">No guest camera photos yet.</div>
        )}
      </main>
    </>
  );
}
